/* The (old) AddressForm with separate entry for each field */
import { styled } from 'styled-components';

import type { ApiAddressWithFormData } from '#/api/types/ApiAddress';
import { Box } from '#/components/Box/Box';
import { InputField } from '#/components/Form/InputField';
import { RadioButtonsField } from '#/components/Form/Radio/RadioButtonsField';
import { SelectInputField } from '#/components/Form/SelectInputField';
import { InfoBox } from '#/components/InfoBox/InfoBox';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import { COUNTRIES } from '#/utils/constants/general/countries';
import { partial } from '#/utils/functional/function';

import { AddressMap } from './AddressMap';
import { AllFieldToggle } from './AllFieldToggle';
import { UseAddressButton } from './UseAddressButton';

// since google autocomplete cant properly unbind from form fields,
// we will just visibly hide them instead of removing the fields and triggering the
// mounting/unmounting behavior
const Hide = styled(Box)<{ $hidden?: boolean }>`
  display: ${(props) => (props.$hidden ? 'none' : 'block')};
`;

type Props = {
  address: ApiAddressWithFormData | null | undefined;
  submitFieldUpdate: (arg0: string, arg1: string) => void;
  index?: number;
  showDescription?: boolean;
  canChangeLocationHidden?: boolean;
  overrideLatLong: (address: { latitude: number; longitude: number }) => void;
  error: string | null | undefined;
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  clearErrors: (...args: any[]) => any;
  disabled: boolean;
  showLocationButtonText?: string;
  hideLocationButtonText?: string;
  useOrgAddress?: () => void;
  toggleSeparateFields: () => void;
  required?: boolean;
};

export function AddressFormSeparate({
  address,
  submitFieldUpdate,
  index,
  showDescription,
  canChangeLocationHidden,
  overrideLatLong,
  error,
  clearErrors,
  disabled,
  showLocationButtonText,
  hideLocationButtonText,
  useOrgAddress,
  toggleSeparateFields,
  required = true,
}: Props) {
  const { i18n } = useI18n();

  const namePrefix = index !== undefined ? `addresses.${index}` : 'address';
  const namePrefixDash = namePrefix.replace(/\./g, '-');
  return (
    <div data-qa-id={`${namePrefixDash}-section`}>
      {error && (
        <InfoBox className="error" $type="danger" $marginBottom={4}>
          {error}
        </InfoBox>
      )}
      {canChangeLocationHidden && (
        <RadioButtonsField
          name={`${namePrefix}.locationHidden`}
          buttons={[
            {
              label: showLocationButtonText ?? i18n('Show full street address'),
              value: false,
            },
            {
              label: hideLocationButtonText ?? i18n('Keep address private'),
              value: true,
            },
          ]}
          disabled={disabled}
          onChange={partial(submitFieldUpdate, 'locationHidden')}
        />
      )}
      {useOrgAddress && (
        // @ts-expect-error TS(2322): Type 'false | -12 | undefined' is not assignable t... Remove this comment to see the full error message
        <Box mt={canChangeLocationHidden && -12}>
          <UseAddressButton
            onClick={useOrgAddress || (() => undefined)}
            disabled={disabled}
          />
        </Box>
      )}
      {showDescription && (
        <InputField
          name={`${namePrefix}.description`}
          label={
            /* TRANSLATORS: Form field for address description */
            i18n('Location name')
          }
          placeholder={
            /* TRANSLATORS: Form field for address description placeholder */
            i18n('e.g. My House, Coffee Shop, The Library (optional)')
          }
          disabled={disabled}
        />
      )}

      <Box position="relative">
        <Box position="absolute" zIndex={1} right={0}>
          <AllFieldToggle
            clearErrors={clearErrors}
            qaId={`${namePrefixDash}-show-one-field`}
            text={i18n('Show single address field')}
            toggleSeparateFields={toggleSeparateFields}
          />
        </Box>
        <SelectInputField
          name={`${namePrefix}.country`}
          label={
            /* TRANSLATORS: Form field for address country */
            `${i18n('Country')}${required ? '*' : ''}`
          }
          options={COUNTRIES(i18n).map(({ id, name }) => ({
            label: name,
            value: id,
          }))}
          disabled={disabled}
          onUpdateValue={partial(submitFieldUpdate, 'country')}
        />
        <Hide $hidden={Boolean(address?.locationHidden)}>
          <InputField
            name={`${namePrefix}.line1`}
            label={
              /* TRANSLATORS: Form field for address line 1 */
              `${i18n('Address Line 1')}${required ? '*' : ''}`
            }
            placeholder={i18n('123 5th Street')}
            disabled={disabled}
            onUpdateValue={partial(submitFieldUpdate, 'line1')}
          />
          <InputField
            name={`${namePrefix}.line2`}
            label={
              /* TRANSLATORS: Form field for address line 2 */
              i18n('Address Line 2')
            }
            placeholder={i18n('e.g., suite 201 (optional)')}
            disabled={disabled}
            onUpdateValue={partial(submitFieldUpdate, 'line2')}
          />
          {!address?.locationHidden && (
            <InputField
              name={`${namePrefix}.city`}
              label={
                /* TRANSLATORS: Form field for address city */
                `${i18n('City')}${required ? '*' : ''}`
              }
              placeholder={i18n('Lake Wobegon')}
              disabled={disabled}
              onUpdateValue={partial(submitFieldUpdate, 'city')}
            />
          )}
        </Hide>
        {address?.locationHidden && (
          <InputField
            name={`${namePrefix}.city`}
            label={
              /* TRANSLATORS: Form field for address city */
              `${i18n('City')}${required ? '*' : ''}`
            }
            placeholder={i18n('Lake Wobegon')}
            disabled={disabled}
            onUpdateValue={partial(submitFieldUpdate, 'city')}
          />
        )}
        <Box display="flex" flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}>
          <Box width={1} pr={[0, 0, 0, '8px']}>
            <InputField
              name={`${namePrefix}.state`}
              label={
                /* TRANSLATORS: Form field for address state/province */
                `${i18n('State/Province')}${required ? '*' : ''}`
              }
              placeholder={i18n('Minnesota')}
              disabled={disabled}
              onUpdateValue={partial(submitFieldUpdate, 'state')}
            />
          </Box>

          <Hide
            width={1}
            $hidden={Boolean(address?.locationHidden)}
            pl={[0, 0, 0, '8px']}
          >
            <InputField
              name={`${namePrefix}.zipcode`}
              label={
                /* TRANSLATORS: Form field for address zip/postal code */
                `${i18n('Zip/Postal Code')}${required ? '*' : ''}`
              }
              placeholder={i18n('12345')}
              disabled={disabled}
              onUpdateValue={partial(submitFieldUpdate, 'zipcode')}
            />
          </Hide>
        </Box>

        {address?.latitude && address?.longitude && (
          <>
            <BodySmall
              qaId={`${namePrefixDash}-latlong-address`}
              mb={2}
              color="lightContentGrey"
            >{`${i18n('Preview of Location')}: ${address.full}`}</BodySmall>
            <AddressMap
              latitude={address.latitude}
              longitude={address.longitude}
              updateAddress={overrideLatLong}
              disabled={disabled}
              qaId={`${namePrefixDash}-map`}
            />
          </>
        )}
      </Box>

      {/* These are just for the tests. */}
      {/* TODO: don't include in production */}
      <input
        type="hidden"
        value={address?.latitude ?? ''}
        data-qa-id={`${namePrefixDash}-latitude`}
      />
      <input
        type="hidden"
        value={address?.longitude ?? ''}
        data-qa-id={`${namePrefixDash}-longitude`}
      />
    </div>
  );
}
